'use client';

import type { ReactElement } from 'react';
import { useTranslations } from '@fshr/context/i18n-context';
import type { ImagePrimitive } from '@fshr/shared/types';
import Image from '../image';
import BevSafeText from '../bev-safe-text';

const DEFAULT_EYEBROW = 'Meet Your Host';

export type Props = {
	hostName: string;
	description: string;
	image: ImagePrimitive;
	eyebrow?: string;
	position?: string;
};

export default function FeaturedProfileCard({
	hostName,
	description,
	image,
	eyebrow,
	position,
}: Props): ReactElement | null {
	const t = useTranslations();

	if (!hostName || !image) return null;

	const visibleEyebrow = eyebrow?.trim() || t(DEFAULT_EYEBROW);

	return (
		// Figma "Wide Grid: Featured Profile Card" binds every value to a named
		// token (card-padding-s, inline-l, stack-xs). Its mobile frame resolves
		// them at the L-tier values, which reads as "flat" - that's the frame's
		// variable mode, not the design intent (see decision 9ed57b).
		// No spacing-wide-grid here, unlike most other "Wide Grid" PDP
		// components: this one is only ever rendered as a beside-aside child
		// (both product-pages/signature and /default wire it with
		// layout: 'beside-aside'), nested inside the content column the
		// template's own grid already constrains. Adding its own page-level
		// gutter on top of that double-counted the inset and collapsed the
		// host text to 0px at narrow beside-aside widths (the "zero-width
		// cliff" template.cy.tsx regression test exists specifically to
		// catch this) - decision 0016's "component owns no padding, page/
		// grid supplies it" pattern, same as CompactItinerary and
		// ExperienceDetailsList right next to it in the same content column.
		<section className="w-full bg-warm-stone/30">
			<div className="flex flex-col p-card-padding-s gap-inline-l md:flex-row md:items-start">
				<div className="flex min-w-0 flex-col gap-y-stack-xs md:flex-1">
					<div className="flex flex-col gap-y-stack-xs">
						<h2 className="text-label">
							<BevSafeText>{visibleEyebrow}</BevSafeText>
						</h2>
						<div className="flex flex-col">
							<h3 className="text-h3">{hostName}</h3>
							{position && <p className="text-caption">{position}</p>}
						</div>
					</div>
					<p className="text-body3">
						<BevSafeText>{description}</BevSafeText>
					</p>
				</div>
				<div className="aspect-[4/5] w-full shrink-0 overflow-hidden md:w-[150px]">
					<Image
						imagePrimitive={image}
						roughSize="s"
						alt={image.alt || hostName}
						className="h-full w-full object-cover"
					/>
				</div>
			</div>
		</section>
	);
}
